import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiRefreshCw, FiUpload, FiDownload, FiTrash2, FiCheck, FiX, FiAlertCircle, FiBell, FiLogOut, FiArrowUp, FiArrowDown, FiClock, FiCheckCircle, FiAlertTriangle, FiClock as FiPending, FiEye, FiCheckSquare, FiStar, FiCalendar } from 'react-icons/fi';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import config from '../config';
import UploadArea from '../components/UploadArea';
import AccellorLogo from '../assets/accellor_logo.svg';
import { useMsal } from "@azure/msal-react";
import Logout from '../assets/logout.svg';
import NotificationDrawer from '../components/NotificationDrawer';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  height: 64px;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
`;

const Logo = styled.img`
  height: 32px;
  cursor: pointer;
`;

const MainContent = styled.div`
  padding: 2rem;
`;

const ContentHeader = styled.div`
  margin-bottom: 2rem;
`;

const SearchAndFilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
`;

const SearchContainer = styled.div`
  flex: 0 0 auto;
  width: 350px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  gap: 0.5rem;
  transition: all 0.2s;

  &:focus-within {
    border-color: #94a3b8;
    box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.1);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: #1e293b;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;

  &[data-action="download"] {
    color: #2563eb;
    background: #eff6ff;
    &:hover {
      background: #dbeafe;
      color: #1d4ed8;
    }
  }

  &[data-action="verify"] {
    color: #0d9488;
    background: #f0fdfa;
    &:hover {
      background: #ccfbf1;
      color: #0f766e;
    }
  }

  &[data-action="delete"] {
    color: #dc2626;
    background: #fef2f2;
    &:hover {
      background: #fee2e2;
      color: #b91c1c;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  flex: 1;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }

  &.active {
    border-color: #2563eb;
    background: #eff6ff;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => {
      switch (props.status) {
        case 'completed': return '#22c55e';
        case 'processing': return '#3b82f6';
        case 'failed': return '#ef4444';
        case 'pending': return '#f59e0b';
        default: return '#94a3b8';
      }
    }};
  }

  .stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 0.75rem;
  }

  .stat-trend {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: ${props => props.trend > 0 ? '#22c55e' : '#ef4444'};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .stat-details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
    font-size: 0.75rem;
    color: #64748b;
  }

  .stat-action {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: #2563eb;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const DocumentsTable = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  height: calc(100vh - 400px); // Adjust based on other elements' height
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
  background: white;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;

  > div {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &.sortable {
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      transition: all 0.2s;
      
      &:hover {
        background: #f1f5f9;
        color: #1e293b;
      }

      .sort-icon {
        opacity: 0.5;
        transition: all 0.2s;
      }

      &:hover .sort-icon {
        opacity: 1;
      }
    }
  }
`;

const TableContent = styled.div`
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }

  ${TableRow} {
    &:last-child {
      border-bottom: none;
    }
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #6b7280;
`;

const FilterContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  margin-left: 4px;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }

  &.active {
    background: #eff6ff;
    color: #2563eb;
  }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  min-width: 350px;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  transform-origin: top right;
  animation: filterDropdownIn 0.2s ease-out;

  @keyframes filterDropdownIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const FilterGroup = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  outline: none;
  transition: all 0.2s;
  background: white;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.5);
  }
`;

const RangeInputs = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  input {
    width: 120px;
  }

  span {
    color: #64748b;
    font-size: 0.875rem;
  }
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem;
  cursor: pointer;
  color: ${props => props.selected ? '#2563eb' : '#64748b'};
  font-size: 0.875rem;
  border-radius: 6px;
  transition: all 0.2s;
  background: ${props => props.selected ? '#eff6ff' : 'transparent'};

  &:hover {
    background: ${props => props.selected ? '#eff6ff' : '#f8fafc'};
    color: ${props => props.selected ? '#2563eb' : '#1e293b'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
`;

const FilterButton = styled.button`
  padding: 0.625rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'clear' && `
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #64748b;

    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
      color: #1e293b;
    }
  `}

  ${props => props.variant === 'apply' && `
    background: #2563eb;
    border: none;
    color: white;

    &:hover {
      background: #1d4ed8;
    }

    &:active {
      transform: translateY(1px);
    }
  `}
`;

const getProgressColor = (status) => {
  switch (status) {
    case 'completed': return '#059669';
    case 'processing': return '#2563eb';
    case 'failed': return '#dc2626';
    case 'pending': return '#d97706';
    default: return '#6b7280';
  }
};

const NotificationBadge = styled.span`
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border-radius: 9999px;
  font-size: 0.75rem;
  color: #374151;
  margin-right: 0.5rem;

  button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: #6b7280;
    display: flex;
    align-items: center;

    &:hover {
      color: #ef4444;
    }
  }
`;

const NotificationsContainer = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 24rem;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  animation: slideIn 0.2s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .icon {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.type === 'error' ? '#ef4444' : '#10b981'};
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  .message {
    color: #111827;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
  }

  .close {
    flex-shrink: 0;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    margin: -0.25rem;
    border-radius: 0.25rem;
    transition: all 0.2s;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  
  ${props => props.clickable && `
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      background: #f1f5f9;
    }
  `}

  ${props => props.align === 'right' && `
    justify-content: flex-end;
    padding-right: 1rem;
  `}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'completed': return '#dcfce7';
      case 'processing': return '#dbeafe';
      case 'failed': return '#fee2e2';
      case 'verified': return '#dcfce7';
      case 'pending': return '#fef3c7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'completed': return '#166534';
      case 'processing': return '#1e40af';
      case 'failed': return '#991b1b';
      case 'verified': return '#166534';
      case 'pending': return '#92400e';
      default: return '#374151';
    }
  }};
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: white;
  border: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  margin-left: 4px;
  transition: all 0.2s;

  &:hover {
    background: #dc2626;
    transform: rotate(45deg);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background: #1e293b;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-5px);
  transition: all 0.2s;
  z-index: 50;
  pointer-events: none;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:before {
    content: '';
    position: absolute;
    top: -4px;
    right: 10px;
    width: 8px;
    height: 8px;
    background: #1e293b;
    transform: rotate(45deg);
  }
`;

const VerificationIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-left: 4px;
  position: relative;

  ${props => props.status === 'pending' && `
    &:before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: #f59e0b;
      opacity: 0.3;
      animation: pulse 1.5s ease-in-out infinite;
    }

    svg {
      color: #92400e;
    }
  `}

  ${props => props.status === 'verified' && `
    svg {
      color: #166534;
    }
  `}

  &:hover ${Tooltip} {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const VerificationLink = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    ${StatusBadge} {
      transform: translateX(-4px);
    }
  }
`;

// Create a separate component for header buttons
const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &.active {
    color: #2563eb;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CompactHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 200;
  transform: translateY(-100%);
  transition: transform 0.3s ease;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  &.visible {
    transform: translateY(0);
  }

  .compact-stats {
    display: flex;
    gap: 1rem;
    margin-right: auto;
  }

  .compact-stat {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;

    .value {
      font-weight: 600;
      color: #1e293b;
    }

    .indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${props => props.color || '#94a3b8'};
    }
  }
`;

const ActionBar = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: #EFF6FF;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 100;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  border: 1px solid #DBEAFE;
  border-left: 4px solid #3B82F6;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  span {
    font-size: 0.875rem;
    color: #1E40AF;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: '';
      display: inline-block;
      width: 20px;
      height: 20px;
      background: #3B82F6;
      border-radius: 50%;
      mask-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>');
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
    }
  }
`;

const shimmerAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

const TableLoadingSkeleton = styled.div`
  ${shimmerAnimation}
  
  .skeleton-row {
    display: grid;
    grid-template-columns: 40px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    align-items: center;
  }

  .skeleton-cell {
    height: 20px;
    background: linear-gradient(90deg, #f1f5f9 0px, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 2000px 100%;
    animation: shimmer 2s linear infinite;
    border-radius: 4px;
  }

  .checkbox-skeleton {
    width: 16px;
    height: 16px;
    background: linear-gradient(90deg, #f1f5f9 0px, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 2000px 100%;
    animation: shimmer 2s linear infinite;
    border-radius: 4px;
  }

  .text-skeleton {
    height: 16px;
    width: 80%;
  }

  .badge-skeleton {
    height: 24px;
    width: 100px;
  }

  .action-skeleton {
    height: 24px;
    width: 24px;
  }
`;

const LoadingTableContent = () => {
  return (
    <TableLoadingSkeleton>
      {[...Array(8)].map((_, index) => (
        <div key={index} className="skeleton-row">
          <div className="checkbox-skeleton" />
          <div className="skeleton-cell text-skeleton" />
          <div className="skeleton-cell text-skeleton" style={{ width: '60%' }} />
          <div className="skeleton-cell text-skeleton" style={{ width: '40%' }} />
          <div className="skeleton-cell badge-skeleton" />
          <div className="skeleton-cell badge-skeleton" />
          <div className="skeleton-cell badge-skeleton" />
          <div className="skeleton-cell text-skeleton" style={{ width: '70%' }} />
          <div className="skeleton-cell text-skeleton" style={{ width: '50%' }} />
          <div className="skeleton-cell text-skeleton" style={{ width: '80%' }} />
          <div className="skeleton-cell action-skeleton" />
        </div>
      ))}
    </TableLoadingSkeleton>
  );
};

const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;

  .star {
    color: #cbd5e1;
    cursor: pointer;
    transition: color 0.2s;

    &.filled {
      color: #f59e0b;
    }

    &:hover {
      transform: scale(1.1);
    }
  }

  .rating-tooltip {
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s;
    white-space: nowrap;
    z-index: 10;

    &:after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
      width: 8px;
      height: 8px;
      background: #1e293b;
    }
  }

  &:hover .rating-tooltip {
    opacity: 1;
    visibility: visible;
    top: -35px;
  }
`;

const MLInferenceProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .progress-circle {
    width: 24px;
    height: 24px;
    position: relative;
  }

  .status-text {
    font-size: 0.875rem;
    color: ${props => {
      switch (props.status?.toLowerCase()) {
        case 'triggered': return '#f59e0b';
        case 'inferencing': return '#3b82f6';
        case 'completed': return '#10b981';
        default: return '#94a3b8';
      }
    }};
  }
`;

const Rating = ({ value, onRate, documentId }) => {
  return (
    <StarRating>
      {[1, 2, 3, 4, 5].map((star) => (
        <FiStar
          key={star}
          size={16}
          className={`star ${value >= star ? 'filled' : ''}`}
          onClick={() => onRate(documentId, star)}
        />
      ))}
      {value && <div className="rating-tooltip">Rating: {value} of 5</div>}
    </StarRating>
  );
};

const MLInference = ({ status }) => {
  const getProgress = () => {
    switch (status?.toLowerCase()) {
      case 'triggered': return 15;
      case 'inferencing': return 65;
      case 'completed': return 100;
      default: return 0;
    }
  };

  return (
    <MLInferenceProgress status={status}>
      <div className="progress-circle">
        <CircularProgressbar
          value={getProgress()}
          strokeWidth={12}
          styles={buildStyles({
            pathColor: status?.toLowerCase() === 'completed' ? '#10b981' :
                      status?.toLowerCase() === 'inferencing' ? '#3b82f6' :
                      status?.toLowerCase() === 'triggered' ? '#f59e0b' : '#94a3b8',
            trailColor: '#e2e8f0',
            strokeLinecap: 'round'
          })}
        />
      </div>
      <span className="status-text">
        {status || 'Not Started'}
      </span>
    </MLInferenceProgress>
  );
};

const ViewDetailsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 6px;
  color: #2563EB;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 4px;
  margin-left: auto;

  &:hover {
    background: #DBEAFE;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  min-height: 24px;
`;

const ActiveFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 9999px;
  font-size: 0.75rem;
  color: #2563EB;
  font-weight: 500;

  .label {
    color: #64748B;
    margin-right: 0.25rem;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: none;
    background: none;
    color: #94A3B8;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;

    &:hover {
      background: #DBEAFE;
      color: #1E40AF;
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const MainDashboard = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    completed: 0,
    processing: 0,
    failed: 0,
    pending: 0
  });
  const { instance } = useMsal();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    pageRange: { min: '', max: '' },
    processingTime: { min: '', max: '' },
    hilStatus: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const filterButtonRef = useRef(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [isRealtime, setIsRealtime] = useState(true);
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterButtonRef.current && !filterButtonRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isRealtime) return;

    const interval = setInterval(() => {
      fetchDocuments();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [isRealtime]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/populate-ui`);
      console.log('API Response:', response.data);
      const docs = Array.isArray(response.data) ? response.data : [];
      setDocuments(docs);
      
      // Calculate stats
      const newStats = docs.reduce((acc, doc) => {
        if (!doc?.status) return acc;
        
        const status = doc.status.toLowerCase();
        if (status === 'completed') acc.completed = (acc.completed || 0) + 1;
        else if (status === 'processing') acc.processing = (acc.processing || 0) + 1;
        else if (status === 'failed') acc.failed = (acc.failed || 0) + 1;
        else acc.pending = (acc.pending || 0) + 1;
        
        return acc;
      }, {
        completed: 0,
        processing: 0,
        failed: 0,
        pending: 0
      });
      
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusCardClick = (status) => {
    setActiveStatusFilter(current => current === status ? null : status);
    setFilters(prev => ({
      ...prev,
      status: prev.status === status ? '' : status
    }));
  };

  const filteredDocuments = documents.filter(doc => {
    // Search term filter
    const matchesSearch = doc?.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
    
    // Status filter
    const matchesStatus = !activeStatusFilter || doc?.status?.toLowerCase() === activeStatusFilter.toLowerCase();
    
    // HIL Status filter
    const matchesHilStatus = !filters.hilStatus || 
      (filters.hilStatus === 'verified' ? doc?.verified : 
       filters.hilStatus === 'pending' ? !doc?.verified : true);
    
    // Page range filter
    const matchesPageRange = 
      (!filters.pageRange.min || (doc?.total_pages >= parseInt(filters.pageRange.min))) &&
      (!filters.pageRange.max || (doc?.total_pages <= parseInt(filters.pageRange.max)));
    
    // Processing time filter
    const matchesProcessingTime = 
      (!filters.processingTime.min || (doc?.processing_time >= parseFloat(filters.processingTime.min))) &&
      (!filters.processingTime.max || (doc?.processing_time <= parseFloat(filters.processingTime.max)));
    
    // Date filter
    const docDate = doc?.created_at ? new Date(doc.created_at) : null;
    const matchesDateRange = 
      (!filters.dateRange.start || !docDate || docDate >= new Date(filters.dateRange.start)) &&
      (!filters.dateRange.end || !docDate || docDate <= new Date(filters.dateRange.end));

    return matchesSearch && matchesStatus && matchesHilStatus && 
           matchesPageRange && matchesProcessingTime && matchesDateRange;
  });

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedDocuments = React.useMemo(() => {
    if (!sortConfig.key) return filteredDocuments;

    return [...filteredDocuments].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'document_name') {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      } else if (sortConfig.key === 'total_pages') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDocuments, sortConfig]);

  const handleCellClick = (doc, type) => {
    if (!doc?.document_id) {
      console.error('Document ID not found:', doc);
      return;
    }
    
    switch(type) {
      case 'verification':
        navigate(`/verify/${doc.document_id}`, {
          state: { documentName: doc.document_name }
        });
        break;
      case 'details':
        navigate(`/document/${doc.document_id}`);
        break;
      default:
        break;
    }
  };

  const handleFilterClick = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? '' : value
    }));
  };

  const handleSelectDoc = (doc) => {
    setSelectedDocs(prev => {
      const isSelected = prev.some(d => d.document_id === doc.document_id);
      if (isSelected) {
        return prev.filter(d => d.document_id !== doc.document_id);
      }
      return [...prev, doc];
    });
  };

  const handleBatchAction = (action) => {
    if (selectedDocs.length === 0) return;

    switch(action) {
      case 'download':
        // Implement download logic
        console.log('Downloading:', selectedDocs);
        break;
      case 'delete':
        // Implement delete logic
        console.log('Deleting:', selectedDocs);
        break;
      case 'verify':
        // Implement verify logic
        console.log('Verifying:', selectedDocs);
        break;
      default:
        break;
    }
  };

  const removeFilter = (filter) => {
    setActiveFilters(prev => prev.filter(f => f !== filter));
    setFilters(prev => ({
      ...prev,
      [filter]: ''
    }));
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    try {
      setLoading(true);
      const response = await axios.post(`${config.API_BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        // Add success notification
        setNotifications(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'success',
            message: `Successfully uploaded ${files.length} document(s)`
          }
        ]);
        // Refresh document list
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      // Add error notification
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'error',
          message: `Failed to upload documents: ${error.message}`
        }
      ]);
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const fileInput = document.getElementById('fileInput');
      fileInput.files = files;
      handleFileUpload({ target: fileInput });
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Add useEffect for auto-removing notifications
  useEffect(() => {
    const timeouts = notifications.map(notification => {
      return setTimeout(() => {
        removeNotification(notification.id);
      }, 5000); // Remove after 5 seconds
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [notifications]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheckCircle size={20} />;
      case 'processing':
        return <FiClock size={20} />;
      case 'failed':
        return <FiAlertTriangle size={20} />;
      case 'pending':
        return <FiPending size={20} />;
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      pageRange: { min: '', max: '' },
      processingTime: { min: '', max: '' },
      hilStatus: '',
    });
  };

  const handleRetryProcessing = async (documentId) => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/retry-processing/${documentId}`);
      
      if (response.data) {
        setNotifications(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'success',
            message: `Successfully initiated reprocessing for document`
          }
        ]);
        
        // Update the document status locally
        setDocuments(prev => prev.map(doc => 
          doc.document_id === documentId 
            ? { ...doc, status: 'processing' }
            : doc
        ));
      }
    } catch (error) {
      console.error('Error retrying document processing:', error);
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'error',
          message: `Failed to retry processing: ${error.response?.data?.detail || error.message}`
        }
      ]);
    }
  };

  const handleRating = async (documentId, rating) => {
    try {
      // Update the rating in your backend
      await axios.post(`${config.API_BASE_URL}/update-rating`, {
        document_id: documentId,
        rating: rating
      });

      // Update the local state
      setDocuments(prev => prev.map(doc => 
        doc.document_id === documentId ? { ...doc, rating } : doc
      ));

      // Show success notification
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          message: 'Rating updated successfully'
        }
      ]);
    } catch (error) {
      console.error('Error updating rating:', error);
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'error',
          message: 'Failed to update rating'
        }
      ]);
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];

    if (filters.dateRange.start) {
      activeFilters.push({
        type: 'dateStart',
        label: 'From',
        value: new Date(filters.dateRange.start).toLocaleDateString()
      });
    }

    if (filters.dateRange.end) {
      activeFilters.push({
        type: 'dateEnd',
        label: 'To',
        value: new Date(filters.dateRange.end).toLocaleDateString()
      });
    }

    if (filters.pageRange.min) {
      activeFilters.push({
        type: 'pageMin',
        label: 'Min Pages',
        value: filters.pageRange.min
      });
    }

    if (filters.pageRange.max) {
      activeFilters.push({
        type: 'pageMax',
        label: 'Max Pages',
        value: filters.pageRange.max
      });
    }

    if (filters.processingTime.min) {
      activeFilters.push({
        type: 'timeMin',
        label: 'Min Time',
        value: `${filters.processingTime.min}s`
      });
    }

    if (filters.processingTime.max) {
      activeFilters.push({
        type: 'timeMax',
        label: 'Max Time',
        value: `${filters.processingTime.max}s`
      });
    }

    if (filters.hilStatus) {
      activeFilters.push({
        type: 'hilStatus',
        label: 'Review Status',
        value: filters.hilStatus.charAt(0).toUpperCase() + filters.hilStatus.slice(1)
      });
    }

    if (activeStatusFilter) {
      activeFilters.push({
        type: 'status',
        label: 'Status',
        value: activeStatusFilter.charAt(0).toUpperCase() + activeStatusFilter.slice(1)
      });
    }

    return activeFilters;
  };

  const removeActiveFilter = (filterType) => {
    switch (filterType) {
      case 'dateStart':
        setFilters(prev => ({
          ...prev,
          dateRange: { ...prev.dateRange, start: '' }
        }));
        break;
      case 'dateEnd':
        setFilters(prev => ({
          ...prev,
          dateRange: { ...prev.dateRange, end: '' }
        }));
        break;
      case 'pageMin':
        setFilters(prev => ({
          ...prev,
          pageRange: { ...prev.pageRange, min: '' }
        }));
        break;
      case 'pageMax':
        setFilters(prev => ({
          ...prev,
          pageRange: { ...prev.pageRange, max: '' }
        }));
        break;
      case 'timeMin':
        setFilters(prev => ({
          ...prev,
          processingTime: { ...prev.processingTime, min: '' }
        }));
        break;
      case 'timeMax':
        setFilters(prev => ({
          ...prev,
          processingTime: { ...prev.processingTime, max: '' }
        }));
        break;
      case 'hilStatus':
        setFilters(prev => ({ ...prev, hilStatus: '' }));
        break;
      case 'status':
        setActiveStatusFilter(null);
        break;
      default:
        break;
    }
  };

  return (
    <DashboardContainer>
      <NotificationDrawer 
        isOpen={isNotificationDrawerOpen}
        onClose={() => setIsNotificationDrawerOpen(false)}
        notifications={notifications}
      />

      <TopBar>
        <LeftSection>
          <Logo src={AccellorLogo} alt="Accellor Logo" onClick={() => navigate('/')} />
        </LeftSection>
        
        <RightSection>
          <HeaderButton 
            className={isRealtime ? 'active' : ''}
            onClick={() => setIsRealtime(!isRealtime)}
            title={isRealtime ? 'Auto-refresh is on' : 'Auto-refresh is off'}
          >
            <FiRefreshCw />
            Auto-refresh
          </HeaderButton>
          
          <HeaderButton 
            title="Notifications"
            onClick={() => setIsNotificationDrawerOpen(true)}
            className={isNotificationDrawerOpen ? 'active' : ''}
          >
            <FiBell />
            {notifications.length > 0 && (
              <NotificationBadge>{notifications.length}</NotificationBadge>
            )}
          </HeaderButton>
          
          <HeaderButton 
            onClick={handleLogout}
            title="Logout"
          >
            <FiLogOut />
            Logout
          </HeaderButton>
        </RightSection>
      </TopBar>

      <CompactHeader className={isScrolled ? 'visible' : ''}>
        <Logo src={AccellorLogo} alt="Accellor Logo" style={{ height: '24px' }} />
        <div className="compact-stats">
          {Object.entries(stats).map(([status, value]) => (
            <div key={status} className="compact-stat">
              <div className="indicator" style={{ 
                background: status === 'completed' ? '#22c55e' 
                  : status === 'processing' ? '#3b82f6'
                  : status === 'failed' ? '#ef4444'
                  : '#f59e0b'
              }} />
              <span className="value">{value}</span>
              <span>{status}</span>
            </div>
          ))}
        </div>
        <ActionButton className="primary" onClick={() => document.getElementById('fileInput').click()}>
          <FiUpload />
          Upload
        </ActionButton>
        <SearchContainer style={{ width: '250px' }}>
          <SearchBar>
            <FiSearch size={16} color="#64748b" />
            <SearchInput
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
        </SearchContainer>
      </CompactHeader>

      <ActionBar className={selectedDocs.length > 0 ? 'visible' : ''}>
        <span>{selectedDocs.length} documents selected</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <ActionButton data-action="download" onClick={() => handleBatchAction('download')}>
            <FiDownload />
            Download
          </ActionButton>
          <ActionButton data-action="verify" onClick={() => handleBatchAction('verify')}>
            <FiCheckSquare />
            Verify
          </ActionButton>
          <ActionButton data-action="delete" onClick={() => handleBatchAction('delete')}>
            <FiTrash2 />
            Delete
          </ActionButton>
        </div>
      </ActionBar>

      <MainContent>
        <ContentHeader>
          <SearchAndFilterBar>
            <SearchRow>
              <StatsGrid>
                {['completed', 'processing', 'failed', 'pending'].map(status => {
                  const value = stats[status] || 0;
                  const isActive = activeStatusFilter === status;
                  // Mock data for demonstration - replace with real data
                  const trend = status === 'completed' ? 12.5 : status === 'failed' ? -5.2 : 0;
                  const timeFrame = '24h';
                  
                  return (
                    <StatCard 
                      key={status}
                      className={isActive ? 'active' : ''}
                      onClick={() => handleStatusCardClick(status)}
                      status={status}
                      trend={trend}
                    >
                      <div className="stat-header">
                        <div className="stat-label">
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </div>
                        {getStatusIcon(status)}
                      </div>
                      <div className="stat-value">{value}</div>
                      <div className="stat-trend">
                        {trend !== 0 && (
                          <>
                            {trend > 0 ? <FiArrowUp /> : <FiArrowDown />}
                            {Math.abs(trend)}% from last {timeFrame}
                          </>
                        )}
                      </div>
                      <div className="stat-details">
                        <span>Last updated: {new Date().toLocaleTimeString()}</span>
                        <div 
                          className="stat-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Add your action handler here
                            console.log(`View ${status} details`);
                          }}
                        >
                          View details →
                        </div>
                      </div>
                    </StatCard>
                  );
                })}
              </StatsGrid>

              <SearchContainer>
                <SearchBar>
                  <FiSearch size={16} color="#64748b" />
                  <SearchInput
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FilterContainer ref={filterButtonRef}>
                    <IconButton 
                      onClick={() => setShowFilters(!showFilters)}
                      title="Filter"
                      className={showFilters ? 'active' : ''}
                    >
                      <FiFilter size={16} />
                    </IconButton>
                    {showFilters && (
                      <FilterDropdown>
                        <FilterGroup>
                          <h4>Date Range</h4>
                          <RangeInputs>
                            <FilterInput
                              type="date"
                              value={filters.dateRange.start}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                dateRange: { ...prev.dateRange, start: e.target.value }
                              }))}
                              placeholder="Start Date"
                            />
                            <span>to</span>
                            <FilterInput
                              type="date"
                              value={filters.dateRange.end}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                dateRange: { ...prev.dateRange, end: e.target.value }
                              }))}
                              placeholder="End Date"
                            />
                          </RangeInputs>
                        </FilterGroup>

                        <FilterGroup>
                          <h4>Page Range</h4>
                          <RangeInputs>
                            <FilterInput
                              type="number"
                              value={filters.pageRange.min}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                pageRange: { ...prev.pageRange, min: e.target.value }
                              }))}
                              placeholder="Min"
                              min="0"
                            />
                            <span>to</span>
                            <FilterInput
                              type="number"
                              value={filters.pageRange.max}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                pageRange: { ...prev.pageRange, max: e.target.value }
                              }))}
                              placeholder="Max"
                              min="0"
                            />
                          </RangeInputs>
                        </FilterGroup>

                        <FilterGroup>
                          <h4>Processing Time (seconds)</h4>
                          <RangeInputs>
                            <FilterInput
                              type="number"
                              value={filters.processingTime.min}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                processingTime: { ...prev.processingTime, min: e.target.value }
                              }))}
                              placeholder="Min"
                              min="0"
                              step="0.1"
                            />
                            <span>to</span>
                            <FilterInput
                              type="number"
                              value={filters.processingTime.max}
                              onChange={(e) => setFilters(prev => ({
                                ...prev,
                                processingTime: { ...prev.processingTime, max: e.target.value }
                              }))}
                              placeholder="Max"
                              min="0"
                              step="0.1"
                            />
                          </RangeInputs>
                        </FilterGroup>

                        <FilterGroup>
                          <h4>HIL Status</h4>
                          <FilterOption
                            selected={filters.hilStatus === 'verified'}
                            onClick={() => setFilters(prev => ({
                              ...prev,
                              hilStatus: prev.hilStatus === 'verified' ? '' : 'verified'
                            }))}
                          >
                            <FiCheck size={16} />
                            Verified
                          </FilterOption>
                          <FilterOption
                            selected={filters.hilStatus === 'pending'}
                            onClick={() => setFilters(prev => ({
                              ...prev,
                              hilStatus: prev.hilStatus === 'pending' ? '' : 'pending'
                            }))}
                          >
                            <FiClock size={16} />
                            Pending
                          </FilterOption>
                        </FilterGroup>

                        <FilterActions>
                          <FilterButton variant="clear" onClick={clearFilters}>
                            Clear All
                          </FilterButton>
                          <FilterButton variant="apply" onClick={() => setShowFilters(false)}>
                            Apply Filters
                          </FilterButton>
                        </FilterActions>
                      </FilterDropdown>
                    )}
                  </FilterContainer>
                </SearchBar>
                <ActiveFiltersContainer>
                  {getActiveFilters().map((filter, index) => (
                    <ActiveFilterChip key={`${filter.type}-${index}`}>
                      <span className="label">{filter.label}:</span>
                      {filter.value}
                      <button onClick={() => removeActiveFilter(filter.type)}>
                        <FiX />
                      </button>
                    </ActiveFilterChip>
                  ))}
                  {getActiveFilters().length > 0 && (
                    <ActiveFilterChip 
                      as="button" 
                      onClick={clearFilters}
                      style={{ 
                        cursor: 'pointer',
                        background: '#F1F5F9',
                        border: '1px solid #E2E8F0',
                        color: '#64748B'
                      }}
                    >
                      Clear all
                      <FiX />
                    </ActiveFilterChip>
                  )}
                </ActiveFiltersContainer>
              </SearchContainer>
            </SearchRow>

            <UploadArea 
              onClick={() => document.getElementById('fileInput').click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={isDragging ? 'dragging' : ''}
            />
            <input
              type="file"
              id="fileInput"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              multiple
              accept=".pdf"
            />
          </SearchAndFilterBar>
        </ContentHeader>

        <DocumentsTable>
          <TableHeader>
            <div>
              <input
                type="checkbox"
                checked={selectedDocs.length === sortedDocuments.length}
                onChange={() => {
                  if (selectedDocs.length === sortedDocuments.length) {
                    setSelectedDocs([]);
                  } else {
                    setSelectedDocs(sortedDocuments);
                  }
                }}
              />
            </div>
            <div className="sortable" onClick={() => handleSort('document_name')}>
              Document Name
              <span className="sort-icon">
                {sortConfig.key === 'document_name' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('total_pages')}>
              Pages
              <span className="sort-icon">
                {sortConfig.key === 'total_pages' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('processing_time')}>
              Processing Time
              <span className="sort-icon">
                {sortConfig.key === 'processing_time' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('status')}>
              Status
              <span className="sort-icon">
                {sortConfig.key === 'status' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('verified')}>
              Review Status
              <span className="sort-icon">
                {sortConfig.key === 'verified' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('ml_inference_status')}>
              ML Inference
              <span className="sort-icon">
                {sortConfig.key === 'ml_inference_status' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('rating')}>
              Rating
              <span className="sort-icon">
                {sortConfig.key === 'rating' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div className="sortable" onClick={() => handleSort('created_at')}>
              <FiCalendar size={14} />
              Date
              <span className="sort-icon">
                {sortConfig.key === 'created_at' ? (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                ) : '↕'}
              </span>
            </div>
            <div></div>
            <div></div>
          </TableHeader>
          <TableContent>
            {loading ? (
              <LoadingTableContent />
            ) : sortedDocuments.length === 0 ? (
              <LoadingOverlay>No documents found</LoadingOverlay>
            ) : (
              sortedDocuments.map((doc) => (
                <TableRow key={doc?.document_id || Math.random()}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedDocs.some(d => d.document_id === doc.document_id)}
                      onChange={() => handleSelectDoc(doc)}
                      onClick={e => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{doc?.document_name || 'Untitled'}</TableCell>
                  <TableCell>{doc?.total_pages || '-'}</TableCell>
                  <TableCell>{doc?.processing_time ? `${doc.processing_time.toFixed(2)}s` : '-'}</TableCell>
                  <TableCell>
                    <StatusBadge status={doc?.status}>
                      {doc?.status || 'Unknown'}
                      {doc?.status?.toLowerCase() === 'failed' && (
                        <RetryButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetryProcessing(doc.document_id);
                          }}
                          title="Retry processing"
                        >
                          <FiRefreshCw />
                        </RetryButton>
                      )}
                    </StatusBadge>
                  </TableCell>
                  <TableCell 
                    clickable 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCellClick(doc, 'verification');
                    }}
                  >
                    <VerificationLink status={doc?.verified ? 'verified' : 'pending'}>
                      <StatusBadge status={doc?.verified ? 'verified' : 'pending'}>
                        {doc?.verified ? 'Verified' : 'Pending'}
                      </StatusBadge>
                      <VerificationIndicator status={doc?.verified ? 'verified' : 'pending'}>
                        {doc?.verified ? (
                          <FiCheckCircle size={16} />
                        ) : (
                          <>
                            <FiClock size={16} />
                            <Tooltip>Click to validate the response</Tooltip>
                          </>
                        )}
                      </VerificationIndicator>
                    </VerificationLink>
                  </TableCell>
                  <TableCell>
                    <MLInference status={doc?.ml_inference_status} />
                  </TableCell>
                  <TableCell>
                    <Rating 
                      value={doc?.rating} 
                      onRate={(docId, rating) => handleRating(docId, rating)}
                      documentId={doc?.document_id}
                    />
                  </TableCell>
                  <TableCell>
                    {doc?.created_at ? new Date(doc.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <ViewDetailsButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCellClick(doc, 'details');
                      }}
                    >
                      View
                      <FiEye />
                    </ViewDetailsButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableContent>
        </DocumentsTable>
      </MainContent>
    </DashboardContainer>
  );
};

export default MainDashboard;